// 금천아 빌려줘 — 대여 주문 목록 (모바일 우선, 데스크톱은 같은 카드가 그리드로)
//
// ⚠️ 상태 색은 rental.css의 변수(--rental-status-*)로만 쓴다. 여기서는 클래스명에
//    상태 코드를 붙일 뿐이고 색 값을 쓰지 않는다.
// ⚠️ 상태 전환은 항상 서버 API로 보낸다 — 다음 단계 판정(next_status)도 서버가 준 값을
//    그대로 쓴다. 프론트에 순서를 또 적으면 규칙이 두 곳으로 갈라진다.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../../api";
import CancelResolveModal from "./CancelResolveModal";
import {
  formatDateShort,
  formatPaymentChange,
  formatRefundBreakdown,
  formatSyncedAt,
  formatWon,
  handoverLabel,
  scheduleLabels,
} from "./format";
import AddonLinkPicker from "./AddonLinkPicker";
import PickupModal from "./PickupModal";
import FulfillmentRows from "./FulfillmentRows";
import HandoverProgress from "./HandoverProgress";
import RentalMemo from "./RentalMemo";
import "./rental.css";

// 탭 정의 — 키는 서버(api.py TABS)와 같아야 한다.
const TABS = [
  { key: "today_delivery", label: "오늘 배송" },
  { key: "today_pickup", label: "오늘 회수" },
  { key: "active", label: "진행중" },
  { key: "completed", label: "배송·회수 완료" },
  { key: "canceled", label: "취소" },
  { key: "all", label: "전체" },
];

// 회수완료는 입력값(상태·차감액·메모)이 필요해 모달로 처리한다.
const PICKED_UP = "PICKED_UP";
// 취소는 진행 흐름 밖이라 "다음 단계"가 없다 — 버튼 문구를 따로 쓴다.
const CANCELED = "CANCELED";
// 입금대기(무통장 입금 전, 2026-10-23) — 회색 카드, 처리 버튼 없음. 입금이 확인되면 동기화가 배송준비중으로 넘긴다.
const AWAITING_PAYMENT = "AWAITING_PAYMENT";

// 원 주문 카드의 "+ 추가 주문 N원" — 취소되지 않은 추가 주문의 결제금액 합(없으면 null)
function addonAmount(order) {
  const amounts = (order.addon_orders_info || [])
    .filter((addon) => addon.status !== CANCELED && addon.amount != null)
    .map((addon) => Number(addon.amount));
  return amounts.length ? amounts.reduce((sum, value) => sum + value, 0) : null;
}


export default function RentalOrderList() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("today_delivery");
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [pickupTarget, setPickupTarget] = useState(null);
  const [resolveTarget, setResolveTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("rental/orders/", { params: { tab } });
      setOrders(res.data?.results || []);
      setCounts(res.data?.counts || {});
      setLastSyncedAt(res.data?.last_synced_at || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    setError("");
    try {
      const res = await api.post("rental/orders/sync/", {});
      setLastSyncedAt(res.data?.last_synced_at || null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "동기화에 실패했습니다.");
    } finally {
      setSyncing(false);
    }
  };

  // 다음 단계 버튼 — 회수완료만 모달을 띄우고, 나머지는 바로 전환한다.
  const handleNext = async (order) => {
    if (!order.next_status) return;
    if (order.next_status === PICKED_UP) {
      setPickupTarget(order);
      return;
    }
    setBusyId(order.id);
    setError("");
    try {
      await api.post(`rental/orders/${order.id}/transition/`, {
        next_status: order.next_status,
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "상태를 바꾸지 못했습니다.");
    } finally {
      setBusyId(null);
    }
  };

  // 송장 발행 — 채번 + 카페24 배송 등록(배송대기). 상태는 그대로다.
  // ⚠️ 발행 전에는 "배송출발"로 넘어갈 수 없다(서버도 막는다). 카페24가 송장 없는
  //    배송 건에서 배송중으로 바로 못 가기 때문이다.
  const handleIssueTracking = async (order) => {
    setBusyId(order.id);
    setError("");
    try {
      await api.post(`rental/orders/${order.id}/issue-tracking/`, {});
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "송장 발행에 실패했습니다.");
    } finally {
      setBusyId(null);
    }
  };

  // 수령 완료(직접 수령·반납) — 송장 등록(방문수령) → 배송출발 → 배송중 → 배송완료.
  // 중간 실패는 200 + ok=false로 온다(앞 단계는 반영됨) — 목록을 다시 불러 진행을 보여준다.
  const handleHandover = async (order) => {
    setBusyId(order.id);
    setError("");
    try {
      const res = await api.post(`rental/orders/${order.id}/handover/`, {});
      if (res.data && !res.data.ok) {
        setError(`수령 완료 중단(${order.receiver_name}): ${res.data.failed} — ${res.data.error}`);
      }
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "수령 완료에 실패했습니다.");
    } finally {
      setBusyId(null);
    }
  };

  // 취소로 정리 — superuser만 버튼이 보인다(서버가 can_resolve_cancel로 판정).
  const handleResolveConfirm = async (payload) => {
    const order = resolveTarget;
    setBusyId(order.id);
    setError("");
    try {
      await api.post(`rental/orders/${order.id}/resolve-cancel/`, payload);
      setResolveTarget(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "취소로 정리하지 못했습니다.");
    } finally {
      setBusyId(null);
    }
  };

  // 메모 저장 후 — 목록 전체를 다시 부르지 않고 그 카드만 서버 응답으로 바꾼다
  // (다시 부르면 다른 카드에서 쓰던 메모 편집이 끊길 수 있다).
  const handleMemoSaved = (updated) => {
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)));
  };

  const handlePickupConfirm = async (payload) => {
    const order = pickupTarget;
    setBusyId(order.id);
    setError("");
    try {
      const res = await api.post(`rental/orders/${order.id}/transition/`, {
        next_status: PICKED_UP,
        ...payload,
      });
      setPickupTarget(null);
      await load();
      if (res.data?.refund_error) setError(res.data.refund_error);
    } catch (e) {
      setError(e?.response?.data?.detail || "회수완료 처리에 실패했습니다.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rental-page">
      <div className="rental-header">
        <h2 className="rental-title">금천아 빌려줘</h2>
        <div className="rental-header-right">
          <span className="rental-synced">{formatSyncedAt(lastSyncedAt)}</span>
          <button type="button" className="rental-btn-sync" onClick={() => navigate("/rental/sales")}>
            매출 현황
          </button>
          <button
            type="button"
            className="rental-btn-sync"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? "동기화 중" : "동기화"}
          </button>
        </div>
      </div>

      <div className="rental-tabs">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`rental-tab${tab === item.key ? " rental-tab--active" : ""}`}
            onClick={() => setTab(item.key)}
          >
            {item.label}
            <span className="rental-tab-count">{counts[item.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {error && <div className="rental-error">{error}</div>}

      {loading ? (
        <div className="rental-empty">불러오는 중…</div>
      ) : orders.length === 0 ? (
        <div className="rental-empty">해당하는 주문이 없습니다.</div>
      ) : (
        <div className="rental-cards">
          {orders.map((order) => (
            <div
              className={`rental-card${order.status === AWAITING_PAYMENT ? " rental-card--awaiting" : ""}`}
              key={order.id}
            >
              <div className="rental-card-top">
                {/* 주문자·수령자는 같아도 둘 다 보여준다 — 다른 경우를 놓치지 않으려면
                    "같으면 한 줄"이 아니라 항상 두 줄이어야 한다. */}
                <div className="rental-card-names">
                  <span className="rental-card-name">
                    <span className="rental-name-label">주문자 :</span>
                    {order.buyer_name || "-"}
                  </span>
                  <span className="rental-card-name">
                    <span className="rental-name-label">수령자 :</span>
                    {order.receiver_name || "-"}
                  </span>
                </div>
                <span className={`rental-badge rental-badge--${order.status}`}>
                  {order.status_label}
                </span>
              </div>

              {/* 상품명 — 백일상·전집이 한 목록에 섞인다. 이름은 서버(상품 설정)가 준다. */}
              {(order.product_name || order.is_twin) && (
                <div className="rental-card-product">
                  {/* 추가 주문(16번)은 원 주문을 제목으로 — 상태도 원 주문을 따라간다 */}
                  {order.is_addon
                    ? `추가 주문 — 원 주문 ${
                        order.parent_info ? `(${order.parent_info.order_id})` : "미연결"
                      }`
                    : order.product_name}
                  {/* 쌍둥이 옵션을 하나라도 고른 주문 — 판정은 서버(options.kit_summary) */}
                  {order.is_twin && <span className="rental-twin-tag">쌍둥이</span>}
                  {/* 주문마다 다른 대여 기간(아기침대) — 서버 rental_period, 다른 상품은 "" */}
                  {order.rental_period && (
                    <span className="rental-period-tag">{order.rental_period}</span>
                  )}
                </div>
              )}

              <div className="rental-card-addr">
                {order.address_full || "주소 없음"}
                {order.is_demo && <span className="rental-demo-tag"> 시드</span>}
              </div>

              {/* 구성 — 항목별 한 줄. O·X·? 클래스(rental-opt--on/off/unknown)로 색을 바꿀 수 있다.
                  라벨·선택 판정의 출처는 서버(services/options.py)다. */}
              <ul className="rental-card-options">
                {(order.card_options || []).map((option) => (
                  <li
                    key={option.slug}
                    className={`rental-card-option rental-opt--${option.slug}`}
                    data-option={option.slug}
                  >
                    <span className="rental-card-option-label">{option.label}</span>
                    {option.kind === "mark" && (
                      <span
                        className={`rental-opt-mark rental-opt--${
                          option.selected === true
                            ? "on"
                            : option.selected === false
                            ? "off"
                            : "unknown"
                        }`}
                        title={
                          option.selected == null
                            ? "옵션 값으로 선택 여부를 판정하지 못했습니다 — 주문 상세에서 확인하세요"
                            : undefined
                        }
                      >
                        {option.mark}
                      </span>
                    )}
                  </li>
                ))}
                {/* 수령·반납 방법 두 줄 — 판정은 서버(fulfillment), 표기는 공용 컴포넌트 */}
                <FulfillmentRows fulfillment={order.fulfillment} order={order} />
              </ul>

              {/* 원 주문 카드 — 붙은 추가 주문(16번) 한 줄씩. 금액은 그 주문 자체의 결제금액.
                  진행중·완료 탭에서는 추가 주문이 따로 카드로 나오지 않으므로(api.GROUPED_TABS)
                  이 줄이 추가 주문 상세로 가는 길이다. */}
              {(order.addon_orders_info || []).map((addon) => (
                <div key={addon.id} className="rental-addon-line">
                  <a
                    href={`/rental/orders/${addon.id}`}
                    className="rental-addon-link"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/rental/orders/${addon.id}`, {
                        state: {
                          tabTitle: `빌려줘 · ${order.receiver_name || addon.order_id} (추가)`,
                        },
                      });
                    }}
                  >
                    추가 주문 ({addon.order_id}, {formatWon(addon.amount)})
                    {addon.status === CANCELED ? " · 취소" : ""}
                  </a>
                </div>
              ))}

              {/* 주문·결제 일자 — 결제일은 카페24 payment_date(없으면 주문일시) */}
              <div className="rental-dates rental-dates--order">
                <span>
                  <span className="rental-date-label">주문</span>
                  {formatDateShort(order.ordered_at)}
                </span>
                <span>
                  <span className="rental-date-label">결제</span>
                  {order.awaiting_payment ? "입금 전" : formatDateShort(order.paid_at)}
                </span>
              </div>

              {/* 결제금액 — 현재 실결제금액(카페24 원본 payment_amount).
                  최초 결제금액과 다를 때만 괄호로 최초·환불을 붙인다(서버 payment_change). */}
              {order.awaiting_payment ? (
                <div className="rental-card__amount">
                  <span className="rental-date-label">결제금액</span>
                  <span className="rental-awaiting-amount">
                    입금 예정 {formatWon(order.awaiting_payment.amount_due)}
                  </span>
                </div>
              ) : order.payment_amount != null && (
                <div className="rental-card__amount">
                  <span className="rental-date-label">결제금액</span>
                  <strong>{formatWon(order.payment_amount)}</strong>
                  {order.payment_change && (
                    <span className="rental-card__amount-change">
                      ({formatPaymentChange(order.payment_change)})
                    </span>
                  )}
                  {/* 붙은 추가 주문 결제금액 합 — 취소된 추가 주문은 빼고 센다 */}
                  {addonAmount(order) != null && (
                    <span className="rental-card__amount-addon">
                      + 추가 주문 {formatWon(addonAmount(order))}
                    </span>
                  )}
                </div>
              )}

              <div className="rental-dates">
                {/* 지연 여부는 서버 판정(delivery_delayed/pickup_delayed)을 그대로 쓴다.
                    직접 수령·반납 주문은 라벨만 "방문 수령 / 방문 반납"으로 바뀐다. */}
                <span className={order.delivery_delayed ? "rental-date--late" : undefined}>
                  <span className="rental-date-label">{scheduleLabels(order).out}</span>
                  {formatDateShort(order.delivery_date)}
                  {order.delivery_delayed && <span className="rental-late-badge">지연</span>}
                </span>
                <span className={order.pickup_delayed ? "rental-date--late" : undefined}>
                  <span className="rental-date-label">{scheduleLabels(order).back}</span>
                  {formatDateShort(order.pickup_date)}
                  {order.pickup_delayed && <span className="rental-late-badge">지연</span>}
                </span>
                {order.tracking_no && (
                  <span title={order.cafe24_tracking_note || undefined}>
                    <span className="rental-date-label">송장</span>
                    {order.tracking_no}
                    {order.cafe24_tracking_note && (
                      <span className="rental-tracking-note"> · {order.cafe24_tracking_note}</span>
                    )}
                  </span>
                )}
              </div>

              {/* 배송메시지 — 카페24 원본 그대로(서버 shipping_message). 없으면 줄을 숨긴다.
                  길어도 자르지 않고 줄바꿈해서 전부 보여준다. */}
              {order.shipping_message && (
                <div className="rental-card__message">
                  <span className="rental-date-label">배송메시지</span>
                  <span className="rental-card__message-text">{order.shipping_message}</span>
                </div>
              )}

              {/* 공동현관 비밀번호 — 주문서 추가항목(서버 door_codes). 배송 나갈 때 카드만 보고 들어가도록
                  번호를 그대로 보여준다. 회수완료·취소면 서버가 빈 목록을 보내 줄이 사라진다.
                  원 주문 카드에는 붙은 추가 주문의 값도 합쳐 온다(번호가 다를 때만 출처를 붙인다). */}
              {(order.door_codes || []).map((door) => (
                <div className="rental-card__message rental-card__door" key={`${door.source}-${door.value}`}>
                  <span className="rental-date-label">공동현관</span>
                  <span className="rental-card__message-text rental-card__door-code">{door.value}</span>
                  {door.source && <span className="rental-door-source">({door.source})</span>}
                </div>
              ))}

              {/* 입금대기 48시간 경과 — 판정은 서버(awaiting_payment.overdue, 주문일시 기준) */}
              {order.awaiting_payment?.overdue && (
                <div className="rental-warn-box rental-warn-box--oneline rental-warn-box--orange">
                  ⚠ 입금 확인 필요 — {order.awaiting_payment.hours}시간 경과
                </div>
              )}

              {/* 세트 수를 넘겨 배송~회수 기간이 겹친 주문 — 판정은 서버(services/availability.py) */}
              {order.schedule_conflict && (
                <div className="rental-warn-box rental-warn-box--oneline rental-card__conflict">
                  ⚠ 일정 중복 — 같은 기간에 세트가 모자랍니다
                </div>
              )}

              {/* 대여 가능 시작일(입고 전 상품) 전으로 잡힌 주문 — 판정은 서버(availability.before_available_from) */}
              {order.before_available_from && (
                <div className="rental-warn-box rental-warn-box--oneline rental-card__conflict">
                  ⚠ 입고 전 날짜 — 대여 가능 시작일 {formatDateShort(order.before_available_from)}보다 앞입니다
                </div>
              )}

              {/* 쌍둥이·추가 주문 경고 — 문구·판정은 서버(serializers.twin_warnings).
                  자동으로 고치지 않는다(범보의자 모순 등은 고객에게 전화로 확인). */}
              {(order.twin_warnings || []).map((warning) => (
                <div
                  key={warning}
                  className="rental-warn-box rental-warn-box--oneline rental-card__conflict"
                >
                  ⚠ {warning}
                </div>
              ))}
              <AddonLinkPicker order={order} onLinked={load} />

              {/* 한 주문에 대여 상품 2개 이상 — ERP는 첫 상품 기준으로만 일정을 잡는다(serializers.multi_product) */}
              {order.multi_product && (
                <div className="rental-warn-box rental-warn-box--oneline rental-card__conflict">
                  ⚠ 상품 여러 개 — 날짜 수동 확인
                </div>
              )}

              {/* 세이펜 보유 수량을 넘겨 기간이 겹친 주문 — 판정은 서버(availability.saypen_conflict_ids) */}
              {order.saypen_conflict && (
                <div className="rental-warn-box rental-warn-box--oneline rental-card__conflict">
                  ⚠ 세이펜 부족 — 같은 기간 세이펜 대여가 보유 수량을 넘습니다
                </div>
              )}

              {/* 배송 후 취소 감지 — 자동 전환하지 않았으니 사람이 판단해야 한다 */}
              {order.needs_cancel_review && (
                <div className="rental-warn-box rental-warn-box--oneline">
                  ⚠ 취소 요청 — 확인 필요
                  {order.cancel_detail ? ` · ${order.cancel_detail}` : ""}
                </div>
              )}

              {/* 정리 버튼은 superuser에게만 — 판정은 서버가 내린다 */}
              {order.can_resolve_cancel && (
                <button
                  type="button"
                  className="rental-btn-resolve"
                  onClick={() => setResolveTarget(order)}
                  disabled={busyId === order.id}
                >
                  취소로 정리
                </button>
              )}

              {/* 카드에서는 한 줄만 — 사유 전문은 상세에서 본다 */}
              {order.needs_manual_refund && (
                <div className="rental-warn-box rental-warn-box--oneline">
                  ⚠ 카페24 환불 수동처리 필요 · 환불액 {formatWon(order.refund_amount)}
                  {formatRefundBreakdown(order) ? ` (${formatRefundBreakdown(order)})` : ""}
                </div>
              )}

              {/* 수령 완료가 중간에 멈춘 주문 — 어디까지 됐는지 */}
              <HandoverProgress handover={order.handover} />

              <div className="rental-card-actions">
                {/* 직접 수령·반납 — 송장 발행·단계 버튼 대신 "수령 완료" 한 번(서버 handover.needed).
                    송장 발행이 먼저 필요한 주문 — 다음 단계 버튼 자리를 발행 버튼이 대신한다 */}
                {order.is_addon ? (
                  // 추가 주문 — 단계 버튼 없음(원 주문을 따라간다)
                  <span className="rental-addon-follow">원 주문 상태를 따라갑니다</span>
                ) : order.handover?.needed ? (
                  <button
                    type="button"
                    className="rental-btn-next rental-btn-handover"
                    onClick={() => handleHandover(order)}
                    disabled={busyId === order.id}
                  >
                    {busyId === order.id ? "처리 중…" : handoverLabel(order)}
                  </button>
                ) : order.needs_tracking_issue ? (
                  <button
                    type="button"
                    className="rental-btn-next rental-btn-issue"
                    onClick={() => handleIssueTracking(order)}
                    disabled={busyId === order.id}
                  >
                    {busyId === order.id ? "발행 중…" : "송장 발행"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`rental-btn-next rental-btn-next--${
                      order.next_status || order.status
                    }`}
                    onClick={() => handleNext(order)}
                    disabled={!order.next_status || busyId === order.id}
                  >
                    {order.next_status
                      ? busyId === order.id
                        ? "처리 중…"
                        : order.next_status_label
                      : order.status === CANCELED
                      ? "취소됨"
                      : order.status === AWAITING_PAYMENT
                      ? "입금대기"
                      : "완료됨"}
                  </button>
                )}

                {order.receiver_phone ? (
                  <a
                    className="rental-btn-icon"
                    href={`tel:${order.receiver_phone}`}
                    title={`${order.receiver_name} ${order.receiver_phone}`}
                  >
                    ☎
                  </a>
                ) : null}

                <button
                  type="button"
                  className="rental-btn-icon"
                  title="상세"
                  onClick={() =>
                    // 탭 제목을 함께 넘긴다 — AppLayout이 state.tabTitle을 최우선으로 쓴다.
                    navigate(`/rental/orders/${order.id}`, {
                      state: {
                        tabTitle: `빌려줘 · ${order.receiver_name || order.order_id}`,
                      },
                    })
                  }
                >
                  ›
                </button>
              </div>

              {/* 담당자 메모 — 상태 버튼 바로 아래. 취소·회수완료 주문에도 보인다. */}
              <RentalMemo order={order} onSaved={handleMemoSaved} view="list" />
            </div>
          ))}
        </div>
      )}

      {resolveTarget && (
        <CancelResolveModal
          order={resolveTarget}
          busy={busyId === resolveTarget.id}
          onClose={() => setResolveTarget(null)}
          onConfirm={handleResolveConfirm}
        />
      )}

      {pickupTarget && (
        <PickupModal
          order={pickupTarget}
          busy={busyId === pickupTarget.id}
          onClose={() => setPickupTarget(null)}
          onConfirm={handlePickupConfirm}
        />
      )}
    </div>
  );
}
